import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { apiErrorMessage, useUpdateAccount } from "@/lib/api";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  accountName: string;
  segment?: string | null;
  region?: string | null;
}

/**
 * Edits an account's name, segment, and region. All three prefill from the
 * account payload. Emptying a previously-set segment/region sends an explicit
 * `null` to clear it; a field that was empty and stays empty is omitted so the
 * stored value is left unchanged (backend treats absent as unchanged, null as
 * clear).
 */
export function EditAccountDialog({
  open,
  onOpenChange,
  accountId,
  accountName,
  segment,
  region,
}: Props) {
  const updateAccount = useUpdateAccount(accountId);
  const [name, setName] = useState(accountName);
  const [segmentValue, setSegmentValue] = useState(segment ?? "");
  const [regionValue, setRegionValue] = useState(region ?? "");
  const [error, setError] = useState("");

  // Re-seed all fields whenever a fresh open (or a different account) occurs.
  useEffect(() => {
    if (open) {
      setName(accountName);
      setSegmentValue(segment ?? "");
      setRegionValue(region ?? "");
      setError("");
    }
  }, [open, accountName, segment, region]);

  // Non-empty → send the value; a previously-set field emptied → null (clear);
  // empty and originally empty → undefined (omit → left unchanged).
  const diffField = (
    current: string,
    initial: string | null | undefined,
  ): string | null | undefined => {
    const trimmed = current.trim();
    if (trimmed) {
      return trimmed;
    }
    return initial ? null : undefined;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await updateAccount.mutateAsync({
        accountName: name.trim(),
        segment: diffField(segmentValue, segment),
        region: diffField(regionValue, region),
      });
      onOpenChange(false);
    } catch (err) {
      setError(apiErrorMessage(err, "Could not update the account."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit account</DialogTitle>
          <DialogDescription>
            Update the account details. Clearing segment or region removes it.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="editAccountName" className="text-foreground text-sm font-medium">
              Account name
            </label>
            <Input
              id="editAccountName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label htmlFor="editAccountSegment" className="text-foreground text-sm font-medium">
                Segment
              </label>
              <Input
                id="editAccountSegment"
                placeholder="Hospital"
                value={segmentValue}
                onChange={(e) => setSegmentValue(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="editAccountRegion" className="text-foreground text-sm font-medium">
                Region
              </label>
              <Input
                id="editAccountRegion"
                placeholder="EMEA"
                value={regionValue}
                onChange={(e) => setRegionValue(e.target.value)}
              />
            </div>
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateAccount.isPending || !name.trim()}>
              {updateAccount.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
